import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import "@fontsource/aileron/100.css"; // there is a bug in the package where 100 and 300 weight files are swapped
import "@fontsource/aileron/300.css";
import "@fontsource/aileron/400.css";
import "@fontsource/aileron/600.css";
import "@fontsource/aileron/700.css";

import { IdentityProvider } from "./components/IdentityProvider";
import { TrueBizApiProvider } from "./api";
import App from "./App";
import { GlobalErrorBoundary } from "./components/ErrorBoundary";
import theme from "./theme";

// disable tracing and replay for now, re-integrate once we get Sentry.captureException
// working reliably
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || "",
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",
  enabled:
    process.env.NODE_ENV === "production" &&
    (process.env.REACT_APP_SENTRY_ENABLED_HOSTNAMES || "app.truebiz.io")
      .split(",")
      .some((h) => window.location.hostname === h.trim()),
});

dayjs.extend(utc);
dayjs.extend(timezone);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const client = new QueryClient();

root.render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <ThemeProvider theme={theme}>
        <IdentityProvider
          type={
            window.localStorage.getItem("TRUEBIZ-API-KEY")
              ? "apiKey"
              : "propelAuth"
          }
          credential={window.localStorage.getItem("TRUEBIZ-API-KEY") || ""}
          persistCredential={(key: string) => {
            window.localStorage.setItem("TRUEBIZ-API-KEY", key);
          }}
          removePersistedCredential={() => {
            window.localStorage.removeItem("TRUEBIZ-API-KEY");
          }}
        >
          <QueryClientProvider client={client}>
            <TrueBizApiProvider>
              <BrowserRouter>
                <CssBaseline />
                <App />
              </BrowserRouter>
            </TrueBizApiProvider>
          </QueryClientProvider>
        </IdentityProvider>
      </ThemeProvider>
    </GlobalErrorBoundary>
  </React.StrictMode>
);
